import type { FC } from "react";
import { useTranslation } from "react-i18next";
import type {
  PaidMediaOverviewRevenueContributionLeader,
  PaidMediaOverviewSummaryPlatform,
} from "src/services/PaidMediaOverviewAPI";
import { overviewPlatformLabel } from "../../utils/PaidMediaOverviewLabels";
import { formatOverviewPct } from "../../utils/PaidMediaOverviewFormat";
import { OverviewMetricTip, OverviewShareBar } from "./OverviewPrimitives";

interface PlatformContributionCardProps {
  platforms: PaidMediaOverviewSummaryPlatform[];
  revenueContributionLeader: PaidMediaOverviewRevenueContributionLeader | null;
}

const PlatformContributionCard: FC<PlatformContributionCardProps> = ({
  platforms,
  revenueContributionLeader,
}) => {
  const { t } = useTranslation();

  const meta = platforms?.find(entry => entry?.platform === "meta_ads");
  const google = platforms?.find(entry => entry?.platform === "google_ads");

  const metaLabel = t("paidMediaOverview.platforms.meta");
  const googleLabel = t("paidMediaOverview.platforms.google");
  const leaderLabel = overviewPlatformLabel(t, revenueContributionLeader?.platform);

  const rows: Array<{
    key: string;
    title: string;
    tooltipId: string;
    meta: number | null;
    google: number | null;
    metaFormatted: string | null;
    googleFormatted: string | null;
  }> = [
    {
      key: "spendShare",
      title: t("paidMediaOverview.metrics.spendShare"),
      tooltipId: "spendShare",
      meta: meta?.spend_share ?? null,
      google: google?.spend_share ?? null,
      metaFormatted: meta?.formatted_spend_share ?? null,
      googleFormatted: google?.formatted_spend_share ?? null,
    },
    {
      key: "revenueShare",
      title: t("paidMediaOverview.metrics.revenueShare"),
      tooltipId: "revenueShare",
      meta: meta?.revenue_share ?? null,
      google: google?.revenue_share ?? null,
      metaFormatted: meta?.formatted_revenue_share ?? null,
      googleFormatted: google?.formatted_revenue_share ?? null,
    },
    {
      key: "purchaseShare",
      title: t("paidMediaOverview.metrics.purchaseShare"),
      tooltipId: "purchaseShare",
      meta: meta?.purchase_share ?? null,
      google: google?.purchase_share ?? null,
      metaFormatted: meta?.formatted_purchase_share ?? null,
      googleFormatted: google?.formatted_purchase_share ?? null,
    },
  ];

  return (
    <article className="paid-media-panel paid-media-overview__contribution-card">
      <div className="paid-media-overview__panel-header">
        <div>
          <h3>{t("paidMediaOverview.contribution.title")}</h3>
          <p className="paid-media-overview__panel-subtitle">
            {t("paidMediaOverview.contribution.subtitle")}
          </p>
        </div>
      </div>
      <div className="paid-media-overview__contribution-rows">
        {rows.map(row => (
          <div key={row.key} className="paid-media-overview__contribution-row">
            <div className="paid-media-overview__contribution-row-title">
              {row.title}
              <OverviewMetricTip id={row.tooltipId} label={row.title} />
            </div>
            <OverviewShareBar
              label={metaLabel}
              value={row.meta}
              variant="meta-ads"
              right={row.metaFormatted ?? undefined}
            />
            <OverviewShareBar
              label={googleLabel}
              value={row.google}
              variant="google-ads"
              right={row.googleFormatted ?? undefined}
            />
          </div>
        ))}
      </div>
      {leaderLabel ? (
        <div className="paid-media-overview__callout paid-media-overview__callout--success">
          <strong>{t("paidMediaOverview.contribution.insightLabel")}</strong>{" "}
          {t("paidMediaOverview.contribution.insightCopy", {
            platform: leaderLabel,
            revenueShare: formatOverviewPct(revenueContributionLeader?.revenue_share, 0),
            spendShare: formatOverviewPct(revenueContributionLeader?.spend_share, 0),
          })}
        </div>
      ) : null}
    </article>
  );
};

export default PlatformContributionCard;
